import React from "react";
import styles from "./IndexingViewer.module.css";
import uiStyles from "../../reportViewers/ReportUIPrimitives.module.css";
import { renderParagraphs, splitLines, stripBullet } from "./utils.jsx";

const SECTION_HEADINGS = [
  "Summary",
  "Details",
  "Focus Pages",
  "URLs with issues",
];

const escapeRegex = (value = "") =>
  value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const captureSections = (markdown = "") => {
  if (!markdown) return {};
  const normalized = markdown.replace(/\r\n/g, "\n");
  const headingPattern = SECTION_HEADINGS.map(escapeRegex).join("|");
  const regex = new RegExp(`^(${headingPattern}):`, "gim");
  const matches = [];
  let match;
  while ((match = regex.exec(normalized)) !== null) {
    matches.push({
      heading: match[1],
      startIndex: regex.lastIndex,
      headingIndex: match.index,
    });
  }

  const sections = {};
  matches.forEach((current, idx) => {
    const end =
      idx + 1 < matches.length
        ? matches[idx + 1].headingIndex
        : normalized.length;
    const block = normalized.slice(current.startIndex, end).trim();
    sections[current.heading.toLowerCase()] = block;
  });
  return sections;
};

const parseDetails = (block = "") => {
  const details = {
    totalSubmitted: null,
    totalIndexed: null,
    totalExcluded: null,
    coverageRatio: null,
  };
  const lines = splitLines(block);
  lines.forEach((line) => {
    const [rawLabel, ...rest] = line.split(":");
    if (!rest.length) return;
    const label = rawLabel.toLowerCase();
    const valueStr = rest.join(":").trim();
    const numeric = parseFloat(valueStr.replace(/[^\d.]/g, ""));
    if (Number.isNaN(numeric)) return;

    if (label.includes("total urls submitted"))
      details.totalSubmitted = numeric;
    if (label.includes("total indexed")) details.totalIndexed = numeric;
    if (label.includes("total excluded")) details.totalExcluded = numeric;
    if (label.includes("coverage ratio")) details.coverageRatio = numeric;
  });

  if (
    details.coverageRatio == null &&
    details.totalIndexed != null &&
    details.totalSubmitted != null &&
    details.totalSubmitted > 0
  ) {
    details.coverageRatio =
      (details.totalIndexed / details.totalSubmitted) * 100;
  }

  return details;
};

const parseFocusPages = (block = "") => {
  const lines = splitLines(block);
  const items = [];

  lines.forEach((line) => {
    const m = line.match(
      /^([A-Za-z]+)\s*→\s*Indexed:\s*([\d.]+),\s*Not Indexed:\s*([\d.]+),\s*Coverage:\s*([\d.]+)%/i,
    );
    if (!m) return;
    items.push({
      label: m[1].trim(),
      indexed: parseFloat(m[2]) || 0,
      notIndexed: parseFloat(m[3]) || 0,
      coverage: parseFloat(m[4]) || 0,
    });
  });

  return items;
};

const parseIssues = (block = "") => {
  const lines = splitLines(block);
  if (!lines.length) return null;

  const line = lines[0];
  const typeMatch = line.match(/^([^:]+):\s*([\d.]+)\s*URLs?/i);
  const result = { issueLabel: null, total: null, breakdown: [] };

  if (typeMatch) {
    result.issueLabel = typeMatch[1].trim();
    result.total = parseFloat(typeMatch[2]) || 0;
  }

  const breakdownMatch = line.match(/\((.+)\)/);
  if (breakdownMatch) {
    breakdownMatch[1].split(",").forEach((piece) => {
      const part = piece.trim();
      const m = part.match(/^([A-Za-z]+)\s+([\d.]+)/);
      if (!m) return;
      result.breakdown.push({
        label: m[1].trim(),
        value: parseFloat(m[2]) || 0,
      });
    });
  }

  return result;
};

const IndexingViewer = ({ markdown }) => {
  if (!markdown) return null;

  const sections = captureSections(markdown);
  if (!Object.keys(sections).length) {
    return (
      <div className={styles.card}>{renderParagraphs(markdown, styles)}</div>
    );
  }

  const summaryLines = splitLines(sections.summary || "").map(stripBullet);
  const details = parseDetails(sections.details || markdown);
  const focusPages = parseFocusPages(sections["focus pages"] || "");
  const issues = parseIssues(sections["urls with issues"] || "");

  return (
    <div className={styles.card}>
      <div className={styles.hero}>
        <div className={styles.heroLeft}>
          <div className={styles.heroIcon} aria-hidden="true">
            <span className={styles.heroGlass} />
          </div>
          <div>
            <h4 className={styles.heroTitle}>Indexing Coverage</h4>
          </div>
        </div>
        <p className={styles.heroSubtitle}>
          Coverage insights from Google Search Console
        </p>
      </div>

      {summaryLines.length > 0 ? (
        <div className={styles.sectionCard}>
          <div className={styles.sectionHeader}>Summary</div>
          <div className={styles.summaryBody}>
            {summaryLines.map((line, idx) => (
              <div key={`summary-${idx}`} className={styles.summaryRow}>
                <span className={uiStyles.summaryCheck} aria-hidden="true" />
                <span>{line}</span>
              </div>
            ))}
          </div>
        </div>
      ) : null}

      <div className={styles.sectionCard}>
        <div className={styles.sectionHeader}>Coverage snapshot</div>
        <div className={styles.metricsRow}>
          {details.totalSubmitted != null ? (
            <div className={styles.metricItem}>
              <div className={styles.metricValue}>{details.totalSubmitted}</div>
              <div className={styles.metricLabel}>Total Submitted</div>
            </div>
          ) : null}
          {details.totalIndexed != null ? (
            <div className={styles.metricItem}>
              <div className={styles.metricValue}>{details.totalIndexed}</div>
              <div className={styles.metricLabel}>Total Indexed</div>
            </div>
          ) : null}
          {details.totalExcluded != null ? (
            <div className={styles.metricItem}>
              <div className={styles.metricValue}>{details.totalExcluded}</div>
              <div className={styles.metricLabel}>Total Excluded</div>
            </div>
          ) : null}
          {details.coverageRatio != null ? (
            <div className={styles.metricItem}>
              <div className={styles.metricValue}>
                {details.coverageRatio.toFixed(1)}%
              </div>
              <div className={styles.metricLabel}>Coverage Ratio</div>
            </div>
          ) : null}
        </div>
      </div>

      {focusPages.length > 0 ? (
        <div className={styles.sectionCard}>
          <div className={styles.sectionHeader}>Focus pages</div>
          <div className={styles.focusTable}>
            <div className={styles.focusHeader}>
              <span>Type</span>
              <span>Indexed</span>
              <span>Not indexed</span>
              <span>Coverage</span>
            </div>
            {focusPages.map((item) => (
              <div key={item.label} className={styles.focusRow}>
                <span>{item.label}</span>
                <span>{item.indexed}</span>
                <span>{item.notIndexed}</span>
                <span>{item.coverage}%</span>
              </div>
            ))}
          </div>
        </div>
      ) : null}

      {issues ? (
        <div className={styles.sectionCard}>
          <div className={styles.sectionHeader}>URLs with issues</div>
          {issues.breakdown.length > 0 ? (
            <div className={styles.issuesRow}>
              {issues.breakdown.map((item) => (
                <div key={item.label} className={styles.issueItem}>
                  <div className={styles.issueValue}>
                    {String(item.value).padStart(2, "0")}
                  </div>
                  <div className={styles.issueLabel}>{item.label}</div>
                </div>
              ))}
            </div>
          ) : null}
        </div>
      ) : null}

      {sections["urls with issues"]
        ? null
        : renderParagraphs(sections.details || "", styles)}
    </div>
  );
};

export default IndexingViewer;
